import React, { useEffect, useMemo } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup.js';
import * as yup from 'yup';
import moment from 'moment';
import { User, useUser, useSendKeycloakVerificationCode } from '../../hooks/api/useUser';
import Toast from '../../components/common/pop-up-message';
import { ProfileFormData, mobileValid } from '../../utils/profile-validation';
import { TimezoneOffsets, useAppSettings } from '../../hooks/api/useAppSettings';
import useAuth from '../../hooks/utils/useAuth';
import { OnboardingLayout } from './common/layout';
import { DatePicker } from '../../components/date-picker';
import { PhoneIconNew } from '../../assets';
import { formatPhoneNumber } from '../../utils/constants';
import { useHistory } from 'react-router-dom';
import { ROUTE_PATH } from '../../routes/route-paths';
import { resolveTheme } from '../../themes';

const INVALID_DATE = 'INVALID_DATE';

const createDefaultUser = (user?: User | null, timezoneOffsets?: TimezoneOffsets) => {
  const birthDate = user?.date_of_birth
    ? moment(user.date_of_birth).isBefore(moment().subtract(100, 'years'))
      ? undefined
      : moment(user.date_of_birth).format('MM/DD/YYYY')
    : undefined;
  const localTimezoneGMTOffset = `${new Date()}`.match(/\sGMT(.{5})\s/)?.[1];
  const localTimezone =
    Object.keys(timezoneOffsets || {}).find(
      (timezoneKey) =>
        // @ts-ignore
        timezoneOffsets?.[timezoneKey] === localTimezoneGMTOffset,
    ) || 'US/Eastern';

  return {
    first_name: user?.first_name,
    last_name: user?.last_name,
    mobile_phone: formatPhoneNumber(user?.mobile_phone?.replace('+1', '') || ''),
    is_text_compatible_phone: user?.is_text_compatible_phone,
    timezone: timezoneOffsets ? (user?.mobile_phone ? user?.timezone : localTimezone) : user?.timezone,
    date_of_birth: birthDate,
    gender: user?.gender,
    pronoun: user?.pronoun,
    race: user?.race,
    relationship: user?.relationship,
    family: user?.family,
  };
};

const validationSchema = yup.object({
  first_name: yup
    .string()
    .trim()
    .required('First Name is required.')
    .test({
      test: (value: string | undefined) => {
        return value ? value.length >= 2 : false;
      },
      exclusive: false,
      message: 'First Name must be 2 characters or more.',
      name: 'Minimum Characters',
    }),
  last_name: yup
    .string()
    .trim()
    .required('Last Name is required.')
    .test({
      test: (value: string | undefined) => {
        return value ? value.length >= 2 : false;
      },
      exclusive: false,
      message: 'Last name must be 2 characters or more.',
      name: 'Minimum Last Name Characters',
    }),
  date_of_birth: yup
    .string()
    .required('Date of birth is required.')
    .test('Invalid DateTime', 'Invalid date format', (value?: string | undefined) => value !== INVALID_DATE)
    .matches(
      /^([0-9]|0[1-9]|1[012])\/(([0-9])|([0-2][0-9])|([3][0-1]))\/\d{4}$/,
      'Date of birth incorrectly formatted.',
    )
    .test('Minimum Age', 'You must be at least 18 years old to use this service.', (value?: string | undefined) => {
      const dob = moment(value, 'MM/DD/YYYY');
      const val = value ? moment().diff(dob, 'years') >= 18 : false;
      return val;
    }),
  mobile_phone: yup
    .string()
    .trim()
    .test('format', 'Phone number can only contain numbers and hyphens.', (value?: string) => {
      if (!value) return true;
      return /^[0-9-]+$/.test(value);
    })
    .test('checkPhone', 'Please enter a 10 digit number.', async (value?: string) => {
      try {
        if (!value) return true;
        const digitsOnly = value.replace(/\D/g, '');
        if (digitsOnly.length === 10) {
          return await mobileValid({ mobile_phone: digitsOnly });
        }
        return false;
      } catch (error) {
        return false;
      }
    }),
});

export const ProfileStep: React.FC = () => {
  console.log('ProfileStep');
  const theme = resolveTheme();
  const { user } = useAuth();
  const { updateUser } = useUser();
  const { data: appSettings } = useAppSettings();
  const history = useHistory();
  const { mutateAsync: sendKeycloakVerificationCode } = useSendKeycloakVerificationCode();
  const mfaRequired = user?.caller_role.active_subscription?.package.client.use_mfa;

  const methods = useForm({
    defaultValues: useMemo(() => {
      return createDefaultUser(user, appSettings?.timezone_offsets);
    }, [user, appSettings?.timezone_offsets]),
    resolver: yupResolver(validationSchema),
    mode: 'onBlur',
    reValidateMode: 'onSubmit',
  });

  const {
    handleSubmit,
    getValues,
    watch,
    control,
    formState: { errors, isSubmitting },
    reset,
    register,
    setValue,
    setError,
  } = methods;

  // Watch form values to make button state reactive
  const watchedFirstName = watch('first_name');
  const watchedLastName = watch('last_name');
  const watchedDateOfBirth = watch('date_of_birth');

  useEffect(() => {
    reset(createDefaultUser(user, appSettings?.timezone_offsets));
  }, [user, reset, appSettings?.timezone_offsets]);

  const submit = async (data: Partial<ProfileFormData>) => {
    const dateOfBirth = moment(getValues('date_of_birth'), 'MM/DD/YYYY').toISOString();

    try {
      await updateUser.mutateAsync({
        first_name: data.first_name,
        last_name: data.last_name,
        date_of_birth: moment(dateOfBirth).format('YYYY-MM-DD'),
        mobile_phone: data.mobile_phone !== '' ? data.mobile_phone : undefined,
        timezone: data.timezone,
      });

      // Navigate to next step based on MFA requirement
      const mobilePhone = data.mobile_phone !== '' ? data.mobile_phone : undefined;
      if (mfaRequired && mobilePhone) {
        // Send verification code and await success before navigating
        try {
          await sendKeycloakVerificationCode({ mobilePhone: mobilePhone.replace(/\D/g, '') });
          history.push(ROUTE_PATH.ONBOARDING_PHONE_VERIFICATION);
        } catch (error) {
          console.error('[ProfileStep] Error sending verification code', error);
          Toast.error('Failed to send verification code. Please try again.');
        }
      } else {
        // Navigate to topic preferences
        history.push(ROUTE_PATH.ONBOARDING_TOPIC_PREFERENCES);
      }
    } catch (error: any) {
      console.error('[ProfileStep] Error in submit', error);
      if (error?.response?.data?.description) {
        if (error.response.data.description.includes('phone')) {
          setError('mobile_phone', { message: error.response.data.description });
        } else {
          Toast.error(error.response.data.description);
        }
      } else {
        Toast.error('An error occurred. Please try again.');
      }
    }
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formattedValue = formatPhoneNumber(e.target.value);
    setValue('mobile_phone', formattedValue);
  };

  const buttonDisabled =
    !watchedFirstName ||
    watchedFirstName.trim() === '' ||
    !watchedLastName ||
    watchedLastName.trim() === '' ||
    !watchedDateOfBirth ||
    watchedDateOfBirth.trim() === '' ||
    !!errors.first_name ||
    !!errors.last_name ||
    !!errors.date_of_birth;

  const onFormSubmit = handleSubmit(
    (data) => {
      submit(data);
    },
    (errors) => {
      console.error('[ProfileStep] Validation failed', errors);
    },
  );

  return (
    <OnboardingLayout>
      <form onSubmit={onFormSubmit} noValidate>
        <div className="space-y-8">
          <div className="space-y-6">
            <h2 className="font-albert-sans text-lg font-bold leading-6 text-black">What's Your Name?</h2>
            <div className="flex space-x-2">
              <div className="relative w-1/2">
                <label
                  htmlFor="first_name"
                  className="font-albert-sans text-label-gray mb-1 block text-sm font-medium leading-5"
                >
                  First Name
                </label>
                <input
                  id="first_name"
                  type="text"
                  data-testid="first-name-input"
                  className="border-input-border-gray h-14 w-full rounded-xl border bg-gray-50 px-3 py-[10px]"
                  placeholder="First name"
                  {...register('first_name')}
                />
                {errors?.first_name?.message && (
                  <p className="mt-1 text-sm text-red-500" role="alert">
                    {errors?.first_name?.message}
                  </p>
                )}
              </div>
              <div className="relative w-1/2">
                <label
                  htmlFor="last_name"
                  className="font-albert-sans text-label-gray mb-1 block text-sm font-medium leading-5"
                >
                  Last Name
                </label>
                <input
                  id="last_name"
                  type="text"
                  aria-label="Last name input"
                  data-testid="last-name-input"
                  className="border-input-border-gray h-14 w-full rounded-xl border bg-gray-50 px-3 py-[10px]"
                  placeholder="Last name"
                  {...register('last_name')}
                />
                {errors?.last_name?.message && (
                  <p className="mt-1 text-sm text-red-500" role="alert">
                    {errors?.last_name?.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="font-albert-sans text-lg font-bold leading-6 text-black">When Were You Born?</h2>
            <div>
              <p className="font-open-sans leading-base text-sm font-normal text-gray-500">
                {theme.name} is available to individuals 18 years of age and older.
              </p>
            </div>
            <div className="relative">
              <label
                htmlFor="date_of_birth"
                className="font-albert-sans text-label-gray mb-1 block text-sm font-medium leading-5"
              >
                Date of Birth (MM/DD/YYYY)
              </label>
              <Controller
                name="date_of_birth"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <DatePicker
                    value={value ?? ''}
                    onChange={(newValue: unknown) => {
                      const momentVal = newValue as { format: (f: string) => string; year?: () => number } | null;
                      if (momentVal) {
                        const date =
                          +momentVal?.format?.('YYYY') < 1000 || !momentVal?.year?.()
                            ? INVALID_DATE
                            : momentVal?.format?.('MM/DD/YYYY');
                        setValue('date_of_birth', date, { shouldValidate: true });
                      } else {
                        setValue('date_of_birth', '', { shouldValidate: true });
                      }
                    }}
                    onOpen={() => {
                      if (value === '' || !value) {
                        return onChange('01/01/1990');
                      }
                    }}
                  />
                )}
              />
              {errors?.date_of_birth?.message && (
                <p className="mt-1 text-sm text-red-500" role="alert">
                  {errors?.date_of_birth?.message}
                </p>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <h2 className="font-albert-sans text-lg font-bold leading-6 text-black">How Can We Contact You?</h2>
            <div>
              <p className="font-open-sans text-sm font-normal leading-[1.5] text-gray-500">
                {theme.name} uses your phone number for text notifications and calls on our service (optional). We will
                verify access in the next step.
              </p>
            </div>
            <div className="relative">
              <label
                htmlFor="phone-number"
                className="font-albert-sans text-label-gray mb-1 block text-sm font-medium leading-5"
              >
                Phone Number
              </label>
              <div className="relative">
                <input
                  id="phone-number"
                  type="text"
                  aria-label="Phone number input"
                  data-testid="phone-number-input"
                  className="border-input-border-gray h-14 w-full rounded-xl border bg-gray-50 px-3 py-[10px] pl-10"
                  placeholder="Enter your phone number"
                  {...register('mobile_phone')}
                  onChange={handlePhoneChange}
                  maxLength={12}
                />
                <img src={PhoneIconNew} alt="" className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2" />
              </div>
              {errors?.mobile_phone?.message && (
                <p className="mt-1 text-sm text-red-500" role="alert">
                  {errors?.mobile_phone?.message}
                </p>
              )}
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="bg-button-primary w-full rounded-full px-7 py-[10px] shadow-[0px_1px_0.5px_0.05px_rgba(29,41,61,0.02)] disabled:cursor-not-allowed disabled:opacity-50"
              disabled={buttonDisabled || isSubmitting}
            >
              <span className="font-albert-sans text-base font-medium leading-5 text-white">Continue</span>
            </button>
          </div>
        </div>
      </form>
    </OnboardingLayout>
  );
};
